import budgetLogo from "@assets/budget/logo-2025-v1.png";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import { KeywordButtonType } from "@components/Button/utils/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import KeywordFilter from "@features/mybt/components/KeywordFilter";
import { KeywordType } from "@features/mybt/components/KeywordFilter/utils/types";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import TemplateI from "@particles/card/templates/TemplateI/TemplateI";
import Listing from "@particles/listing/Listing";
import Infographic from "@routes/SingaporeBudget/2025/components/Infographics";
import Podcasts from "@routes/SingaporeBudget/2025/components/Podcasts/Podcasts";
import Videos from "@routes/SingaporeBudget/2025/components/Videos";
import Whatsapp from "@routes/SingaporeBudget/2025/components/Whatsapp";
import LatestStories from "@routes/SingaporeBudget/components/LatestStories/LatestStories";
import ViewsStories from "@routes/SingaporeBudget/components/ViewsStories/ViewsStories";
import { Budget2025Data } from "@routes/SingaporeBudget/utils/types";

import Booklet from "./components/Booklet";

/**
 * SingaporeBudget2025 component to display the Singapore Budget 2025 page.
 */
const SingaporeBudget2025 = ({
  highlights,
  latestNews,
  views,
  relatedStories,
  podcastsArticle,
  mtiArticle,
  infographicArticle,
  seoFooterData,
  entity: { tag },
  gaProps,
}: Budget2025Data & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  return (
    <div className="w-full" data-testid="singapore-budget-2025-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header parentCategory={"keywords/singapore-budget-2025"} />

      <PulseEntry />

      <Container rootClassName="pt-6">
        <div className="mb-6 flex items-center justify-between border-b border-gray-850 pb-3">
          <img
            src={budgetLogo}
            width={150}
            height={60}
            alt="Singapore Budget 2025"
            className="w-[103px] lg:w-[150px]"
          />

          <KeywordFilter
            keywordButtonType={KeywordButtonType.version2}
            tag={tag}
            className="inline-block"
            keywordType={KeywordType.noTag}
          />
        </div>

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="lg:col-span-8">
            <TemplateI cardProps={highlights} />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            {infographicArticle ? (
              <Infographic
                cardProp={infographicArticle}
                className="mb-10 border-t border-gray-850 pt-2 lg:border-0"
              />
            ) : null}

            <LatestStories
              cardProps={latestNews}
              className="mb-6 border-t border-gray-850 pt-2"
            />

            {mtiArticle ? (
              <Booklet
                cardProp={mtiArticle}
                className="mb-10 border-t border-gray-850 pt-2"
              />
            ) : null}
          </div>
        </div>

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="lg:col-span-8 lg:pr-3">
            <ViewsStories
              cardProps={views}
              className="border-t border-gray-850 pt-2"
            />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <Whatsapp />
          </div>
        </div>

        <Videos rootClassName="border-t border-gray-850 pt-2 mb-10" />

        <>
          {podcastsArticle ? (
            <Podcasts
              cardProps={podcastsArticle}
              className="border-top mb-10 border-gray-850 pt-2"
            />
          ) : null}
        </>

        <div className="mb-10 grid lg:grid-cols-12">
          <div className="border-t border-gray-850 pt-2 lg:col-span-8 lg:pr-3">
            <BlockTitle text="RELATED STORIES" />

            <Listing
              cardProps={relatedStories}
              pageType={"keyword"}
              size={10}
              urlPath={tag.urlPath}
              offset={3}
            />
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <Ad
              adType="imu2"
              className="flex items-center justify-center lg:sticky lg:top-[70px]"
            />
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default SingaporeBudget2025;
